import { Dialog, DialogActions, DialogContent } from '@mui/material';
import Cropper from 'react-easy-crop';
import React from 'react';
import CustomButton from './CustomButton';
import { getCroppedImgN } from '../Utils/Cropper';

interface Props {
  image: string;
  setImgUrl: any;
  handleCroppedImage: any;
  ratio?: any;
}

/**
 * Image cropping component that allows users to select and crop an image to a specified aspect ratio.
 * @example
 * ImageCropper({ image: someImage, setImgUrl: setImageUrlFunction, handleCroppedImage: handleImageFunction, ratio: 16/9 })
 * Renders a dialog with the cropping interface.
 * @param {Object} { image, setImgUrl, handleCroppedImage, ratio } - Destructured properties from Props.
 * @param {string} image - The URL or source of the image to be cropped.
 * @param {function} setImgUrl - Function to reset the image URL, typically to null.
 * @param {function} handleCroppedImage - Callback function to handle the return of the cropped image data.
 * @param {number} [ratio=16/9] - Aspect ratio to maintain while cropping.
 * @returns {JSX.Element} Returns a dialog component featuring the cropping tool with controls.
 * @description
 *   - Utilizes the 'Cropper' component from React to provide image cropping functionality.
 *   - Maintains crop state and updates it as the user interacts with the cropper.
 *   - Supports zoom and repositioning while cropping the image.
 */
export default function ImageCropper({
  image,
  setImgUrl,
  handleCroppedImage,
  ratio,
}: Props) {
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState(null);
  const [zoom, setZoom] = React.useState(1);
  const onCropComplete = React.useCallback(
    (croppedArea: any, croppedAreaPixels: any) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    [],
  );

  const handleCropFinish = async () => {
    const reslt = await getCroppedImgN(image, croppedAreaPixels);
    handleCroppedImage(reslt);
    setImgUrl(null);
  };
  return (
    <Dialog open={true} fullWidth={true} className="imageCropper">
      <div className="dialogWrapper">
        <div className="dialogHeader">
          <h4>Image Crop</h4>
        </div>
        <DialogContent
          sx={{
            // minHeight: '300px',
            height: '60vh',
          }}
        >
          {/* <Select value={currentCropper} onChange={(e) => setCurrentCropper(e.target.value )}>
              <option value={16 / 9}>Landscape</option>
              <option value={9 / 16}>Portrait</option>
            </Select> */}
          <div
            style={{
              maxWidth: '100%',
              display: 'block',
              position: 'relative',
              minHeight: '100%',
            }}
          >
            <Cropper
              image={image}
              crop={crop}
              zoom={zoom}
              minZoom={0.4}
              aspect={ratio ? ratio : 16 / 9}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              restrictPosition={false}
              // objectFit={ho}
              // cropSize={ {
              //   width: 1080,
              //   height: 1920
              // }}
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider' }}>
          <div className="formButtons end">
            <CustomButton
              onClick={() => {
                setImgUrl(null);
              }}
              size="large"
              variant="outlined"
              text="Cancel"
              width="auto"
              type="button"
              id="cancel"
              showIcon={false}
            />

            <CustomButton
              size="large"
              variant="contained"
              text={'Crop and Upload'}
              width="auto"
              type="submit"
              id="submit"
              disabled={false}
              showIcon={false}
              onClick={handleCropFinish}
            />
          </div>
        </DialogActions>
      </div>
    </Dialog>
  );
}
